import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { ExternalServiceLog, IExternalServiceLog } from '../models/external-service-log.model';

export interface ExternalServiceLogFilters {
  service?: string;
  neighborhoodId?: string;
  startDate?: string;
  endDate?: string;
  statusCode?: string;
  success?: string;
  direction?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExternalServiceLogService {

  public loading: boolean = false;
  public logs: ExternalServiceLog[] = [];
  public total: number = 0;

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  // region get
  async get(
    externalServiceLogId: string
  ): Promise<ExternalServiceLog | null> {
    let log: ExternalServiceLog | null = null;
    const urlApi = `${environment.urlApi}/external-service-logs/${externalServiceLogId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo registro';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ externalServiceLog: IExternalServiceLog }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ externalServiceLog: IExternalServiceLog }>>(urlApi, {
            headers: {
              'content-type': 'application/json',
              'Authorization': token.toString()
            }
          }).subscribe({
            next: (response: IServerResponse<{ externalServiceLog: IExternalServiceLog }>) => resolve(
              new ServerResponse<{ externalServiceLog: IExternalServiceLog }>(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if (response.statusCode === 200) {
        log = new ExternalServiceLog(response.data!.externalServiceLog);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      console.log(error);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return log;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region getAll
  async getAll(
    filters: ExternalServiceLogFilters,
    page: number,
    pageSize: number
  ): Promise<void> {
    this.loading = true;

    const params: string[] = [
      `page=${page}`,
      `page_size=${pageSize}`
    ];

    if (filters.service) params.push(`service=${filters.service}`);
    if (filters.neighborhoodId) params.push(`neighborhood_id=${filters.neighborhoodId}`);
    if (filters.startDate) params.push(`start_date=${filters.startDate}`);
    if (filters.endDate) params.push(`end_date=${filters.endDate}`);
    if (filters.statusCode) params.push(`status_code=${filters.statusCode}`);
    if (
      filters.success !== undefined &&
      filters.success !== null &&
      filters.success !== ''
    ) {
      params.push(`success=${filters.success}`);
    }
    if (filters.direction) params.push(`direction=${filters.direction}`);

    const urlApi = `${environment.urlApi}/external-service-logs?${params.join('&')}`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{
        externalServiceLogs: IExternalServiceLog[];
        total: number;
        page: number;
        pageSize: number;
      }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{
            externalServiceLogs: IExternalServiceLog[];
            total: number;
            page: number;
            pageSize: number;
          }>>(urlApi, {
            headers: {
              'content-type': 'application/json',
              'Authorization': token.toString()
            }
          }).subscribe({
            next: (response) => resolve(
              new ServerResponse<{
                externalServiceLogs: IExternalServiceLog[];
                total: number;
                page: number;
                pageSize: number;
              }>(response)
            ),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if (response.statusCode === 200) {
        this.logs = response.data!.externalServiceLogs.map(
          item => new ExternalServiceLog(item)
        );
        this.total = response.data!.total;
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      console.log(error);
    }

    this.loading = false;
  }

}
